import BaseService from "./BaseService";
import AppStorage from "../storage/AppStorage";
import AppService from "./AppService";
import Config from "./Config";
import { InAppReview } from "@capacitor-community/in-app-review";
import { ModalComponentService } from "./ModalComponentService";
import AppLifecycleService from "./AppLifecycleService";
import type { LifecycleHandler } from "./AppLifecycleService";

/**
 * Service to track consecutive app opening days and prompt the user to rate
 * the app after the configured streak is reached.
 */
class RateAppService extends BaseService {

    static appActiveHandler: LifecycleHandler = () => {
        this.checkAndPrompt();
    }

    /**
     * Registers the app active handler used to re-evaluate the rate flow
     * after the app returns to the foreground.
     */
    static initAppActiveHandler(): void {
        AppLifecycleService.addAppActiveHandler(this.appActiveHandler);
    }

    /**
     * Registers today's app activity in localStorage.
     * Keeps only consecutive active days and resets the streak when there is
     * a gap greater than one day.
     */
    static registerActivity(): void {
        const data = AppStorage.getRateAppData();
        const today = this.getTodayString();

        if (data.dates.length === 0) {
            data.dates = [today];
            AppStorage.setRateAppData(data);
            return;
        }

        const lastDate = data.dates[data.dates.length - 1];
        if (lastDate === today) {
            return;
        }

        if (this.isNextCalendarDay(lastDate, today)) {
            data.dates.push(today);
        } else {
            data.dates = [today];
        }

        const consecutiveDays = Config.getRateAppConsecutiveDays();
        if (data.dates.length > consecutiveDays) {
            data.dates = data.dates.slice(-consecutiveDays);
        }

        AppStorage.setRateAppData(data);
    }

    /**
     * Checks if the user has met the consecutive-days condition to be prompted
     * to rate the app.
     */
    static hasMetConditions(): boolean {
        const data = AppStorage.getRateAppData();
        const minDays = Config.getRateAppConsecutiveDays();

        return data.dates.length >= minDays;
    }

    /**
     * Returns today's date as a string in YYYY-MM-DD format.
     */
    static getTodayString(): string {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Checks whether toDate is the calendar day immediately after fromDate.
     *
     */
    static isNextCalendarDay(fromDate: string, toDate: string): boolean {
        const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
        const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
        const fromTime = Date.UTC(fromYear, fromMonth - 1, fromDay);
        const toTime = Date.UTC(toYear, toMonth - 1, toDay);

        return toTime > fromTime && Math.round((toTime - fromTime) / (1000 * 60 * 60 * 24)) === 1;
    }

    /**
     * Checks whether fromDate is the same day as or before toDate.
     *
     */
    static isSameDayOrBefore(fromDate: string, toDate: string): boolean {
        return fromDate <= toDate;
    }

    /**
    * Registers the current app activity and controls the rate flow on app
    * start and whenever the app becomes active again.
     * If the user already rated, nothing else happens.
     * If a deferred review date exists and has been reached, the custom rate
     * dialog is shown again.
     * Otherwise, if the consecutive-days condition is met, the custom rate app
     * dialog is opened.
     * Does nothing if not running as a native app.
     */
    static checkAndPrompt(): void {
        if (!AppService.isNativeApp()) {
            return;
        }

        this.registerActivity();
        const data = AppStorage.getRateAppData();

        if (data.rated) {
            return;
        }

        if (data.deferredReviewDate) {
            if (!this.shouldOpenDeferredReview(data.deferredReviewDate)) {
                return;
            }

            ModalComponentService.openModalComponent(
                ModalComponentService.ModalComponentTypes.RATE_APP_CARD
            );
            return;
        }

        if (!this.hasMetConditions()) {
            return;
        }

        ModalComponentService.openModalComponent(
            ModalComponentService.ModalComponentTypes.RATE_APP_CARD
        );
    }

    /**
     * Triggers the native in-app review dialog and marks the user as rated,
     * preventing future auto-triggers and future custom modal prompts.
     * Does nothing if not running as a native app.
     */
    static async rateApp(): Promise<void> {
        if (!AppService.isNativeApp()) {
            return;
        }

        await InAppReview.requestReview();

        const data = AppStorage.getRateAppData();
        data.rated = true;
        data.deferredReviewDate = null;
        AppStorage.setRateAppData(data);

        ModalComponentService.reset();
    }

    /**
     * Closes the custom dialog without rating.
     * On the first dismiss, schedules the dialog to be shown again after the
     * configured number of days.
     * On the second dismiss after that deferred date, it marks the user as rated
     * to avoid showing the automatic dialog again.
     */
    static dismiss(): void {
        const data = AppStorage.getRateAppData();

        if (data.deferredReviewDate && this.shouldOpenDeferredReview(data.deferredReviewDate)) {
            data.rated = true;
            data.deferredReviewDate = null;
            AppStorage.setRateAppData(data);
            ModalComponentService.reset();
            return;
        }

        data.deferredReviewDate = this.addDays(this.getTodayString(), Config.getRateAppDeferredDays());
        AppStorage.setRateAppData(data);

        ModalComponentService.reset();
    }

    /**
     * Checks whether the deferred native review date has been reached.
     *
     */
    static shouldOpenDeferredReview(deferredReviewDate: string): boolean {
        return this.isSameDayOrBefore(deferredReviewDate, this.getTodayString());
    }

    /**
     * Adds whole days to a YYYY-MM-DD date.
     *
     */
    static addDays(dateString: string, days: number): string {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        date.setUTCDate(date.getUTCDate() + days);

        const nextYear = date.getUTCFullYear();
        const nextMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
        const nextDay = String(date.getUTCDate()).padStart(2, '0');

        return `${nextYear}-${nextMonth}-${nextDay}`;
    }
}

export default RateAppService;

